import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PageHeader } from '../../app/PageHeader'
import copyJson from '../../demo/fixtures/copy.json'
import createFormJson from '../../demo/fixtures/create-form.json'
import { demoProducts, useDemoStore } from '../../demo/store'
import type { Audience, Budget, Market, Objective } from '../../demo/types'
import { CreativeReady } from './CreativeReady'
import styles from './CreatePage.module.css'
import { GenerateOverlay } from './GenerateOverlay'
import { LinkField } from './LinkField'
import { PlanDraft } from './PlanDraft'
import { INITIAL_PLAN_DRAFT, toCampaignDraft, type PlanDraftFieldKey, type PlanDraftState } from './planDraftData'
import { fillProcessTemplate, scrapeProduct } from './scrape'
import { SummaryCard } from './SummaryCard'

const product = demoProducts.find((item) => item.id === 'p-mug')!

function validLink(value: string): boolean {
  const trimmed = value.trim()
  return trimmed.includes('.') && !trimmed.includes(' ')
}

export function CreatePage() {
  const navigate = useNavigate()
  const chainCreateLink = useDemoStore((state) => state.chainCreateLink)
  const chainBusy = useDemoStore((state) => state.chainBusy)
  const setChainCreateLink = useDemoStore((state) => state.setChainCreateLink)
  const setChainBusy = useDemoStore((state) => state.setChainBusy)
  const createCampaign = useDemoStore((state) => state.createCampaign)
  const [draft, setDraft] = useState<PlanDraftState>(() => structuredClone(INITIAL_PLAN_DRAFT))
  const [touched, setTouched] = useState<Set<PlanDraftFieldKey>>(() => new Set())
  const [openEditor, setOpenEditor] = useState<PlanDraftFieldKey | 'all' | null>(null)
  const [activeStep, setActiveStep] = useState(0)
  const [recognitionStepCount, setRecognitionStepCount] = useState(0)
  const recognitionToken = useRef(0)

  const url = chainCreateLink.url

  const recognize = useCallback((nextValue?: string) => {
    const candidate = (nextValue ?? url).trim()
    const token = ++recognitionToken.current
    if (!validLink(candidate)) {
      setChainCreateLink('error', candidate)
      setRecognitionStepCount(0)
      return
    }
    setChainCreateLink('loading', candidate)
    setRecognitionStepCount(1)
    void (async () => {
      for (let index = 1; index <= createFormJson.recognizeSteps.length; index += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, createFormJson.recognizeStepMs))
        if (recognitionToken.current !== token) return
        if (index < createFormJson.recognizeSteps.length) setRecognitionStepCount(index + 1)
      }
      setChainCreateLink('recognized', candidate)
    })()
  }, [setChainCreateLink, url])

  const changeUrl = (value: string) => {
    recognitionToken.current += 1
    setChainCreateLink('idle', value)
    setRecognitionStepCount(0)
  }

  const changeDraft = (key: PlanDraftFieldKey, value: Objective | Market[] | Audience | Budget) => {
    setDraft((current) => ({ ...current, [key]: value }))
    setTouched((current) => new Set(current).add(key))
  }

  const toggleEditor = (key: PlanDraftFieldKey | 'all') => {
    setOpenEditor((current) => current === key ? null : key)
  }

  const confirm = async () => {
    if (chainCreateLink.state !== 'recognized' || chainBusy) return
    setChainBusy(true)
    let campaignId: string
    try {
      for (let index = 0; index < createFormJson.generateSteps.length; index += 1) {
        setActiveStep(index)
        await new Promise((resolve) => window.setTimeout(resolve, createFormJson.generateSteps[index]!.ms))
      }
      setActiveStep(createFormJson.generateSteps.length)
      campaignId = createCampaign(toCampaignDraft(draft))
    } finally {
      setChainBusy(false)
    }
    void navigate(`/monitor/${campaignId}`)
  }

  const recognized = chainCreateLink.state === 'recognized'
  const facts = scrapeProduct(product.id, url)
  const recognitionSteps = createFormJson.recognizeSteps.map((step) => ({
    label: fillProcessTemplate(step.label, facts),
    detail: fillProcessTemplate(step.detail, facts),
  }))
  const generateSteps = createFormJson.generateSteps.map((step) => ({
    ...step,
    detail: fillProcessTemplate(step.detail, facts),
  }))
  return (
    <div className={styles.page} data-scope={!recognized ? 'state-create-empty' : undefined}>
      <PageHeader data-scope="create-header" title={copyJson.pages.create.title} subtitle={copyJson.pages.create.subtitle} />
      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <LinkField value={url} state={chainCreateLink.state} product={product} facts={facts} visibleStepCount={recognitionStepCount} steps={recognitionSteps} onChange={changeUrl} onRecognize={recognize} />
          {recognized ? <PlanDraft draft={draft} touched={touched} openEditor={openEditor} busy={chainBusy} onToggle={toggleEditor} onChange={changeDraft} onConfirm={() => void confirm()} /> : null}
        </div>
        {recognized ? (
          <aside className={styles.sideColumn}>
            <SummaryCard objective={draft.objective} />
            <CreativeReady />
          </aside>
        ) : null}
      </div>
      {chainBusy ? <GenerateOverlay activeStep={activeStep} steps={generateSteps} /> : null}
    </div>
  )
}
